/// <reference path="../../types/window.d.ts" />
import React, { createContext, useContext, useEffect, useMemo } from 'react';

import { useQueryParams } from '../../utils/router';

/**
 * @typedef {object} FlagConfig
 * @property {string | boolean} defaultValue
 */

/**
 * @type {{
 *   DisableAuth?: FlagConfig
 * }}
 */
const FLAG_CONFIGS = {
  // Only add DisableAuth in development; too risky to have it in production
  ...(process.env.NODE_ENV === 'development'
    ? {
        DisableAuth: {
          defaultValue: process.env.DISABLE_AUTH ?? '',
        },
      }
    : {}),
};

/**
 * @typedef {keyof typeof FLAG_CONFIGS} FlagName
 * @typedef {Record<FlagName, boolean>} FeatureFlags
 */

/** @type {readonly FlagName[]} */
const FLAG_NAMES = /** @type {FlagName[]} */ (Object.keys(FLAG_CONFIGS));

const isTruthy = (v = '') => ['true', '1'].includes(String(v).toLowerCase());

const FeatureFlagsContext = createContext(
  /** @type {FeatureFlags | undefined} */ (undefined),
);

export const useFeatureFlags = () => {
  const context = useContext(FeatureFlagsContext);
  if (!context) {
    throw new Error(
      'useFeatureFlags must be used within a FeatureFlagsProvider',
    );
  }
  return context;
};

/**
 * Feature Flag Priority (highest to lowest):
 * 1. URL query (?flag=true) - sets localStorage and takes precedence
 * 2. localStorage - sticky across sessions until cleared
 * 3. Environment variable - fallback default
 *
 * Flags are "sticky" - once set via URL, they persist in localStorage.
 * @param {React.PropsWithChildren<{}>} props
 */
export const FeatureFlagsProvider = ({ children }) => {
  const flagQueryParams = useQueryParams(...FLAG_NAMES);

  const featureFlags = useMemo(() => {
    return /** @type {FeatureFlags} */ (
      Object.fromEntries(
        FLAG_NAMES.map(
          /**
           * @returns {[FlagName, boolean]}
           */
          (flag) => {
            const config = FLAG_CONFIGS[flag];

            // 1) URL query param (highest priority) + persist
            const queryValue = flagQueryParams[flag];
            if (queryValue !== null) {
              const enabled = isTruthy(queryValue);
              try {
                localStorage.setItem(flag, String(enabled));
              } catch {}
              return [flag, enabled];
            }

            // 2) localStorage
            try {
              const stored = localStorage.getItem(flag);
              if (stored !== null) {
                return [flag, isTruthy(stored)];
              }
            } catch {}

            // 3) env default
            const envVal = config?.defaultValue ?? '';
            return [flag, envVal ? isTruthy(String(envVal)) : false];
          },
        ),
      )
    );
  }, [flagQueryParams]);

  useEffect(() => {
    window._featureFlags = featureFlags;

    if (process.env.NODE_ENV === 'development') {
      const enabledFlags = /** @type {FlagName[]} */ (
        Object.entries(featureFlags)
          .filter(([, isEnabled]) => isEnabled)
          .map(([flagName]) => flagName)
      );

      if (enabledFlags.length > 0) {
        console.groupCollapsed(
          `🚩 Feature Flags: ${JSON.stringify(featureFlags)} (click to expand for tips)`,
        );
        console.info('💡 How to disable flags:');
        enabledFlags.forEach((flagName) => {
          console.info(
            `• ${flagName}: Add ?${flagName}=false to the URL or run localStorage.setItem('${flagName}', 'false')`,
          );
        });
        console.groupEnd();
      } else {
        console.log('🚩 Feature Flags:', featureFlags);
      }
    }
  }, [featureFlags]);

  return (
    <FeatureFlagsContext.Provider value={featureFlags}>
      {children}
    </FeatureFlagsContext.Provider>
  );
};

export default FeatureFlagsProvider;
